import React, { useEffect, useState } from "react";
import * as finService from "../../services/finService";
import useModuleAccess from "../../hooks/useModuleAccess";
import { hasModulePermission } from "../../constants/platformModules";
import useFinOptions from "./useFinOptions";
import useGlContext, { fmtMoney } from "./gl/glContext";
import LinesEditor, { blankLine, lineTotals, linesForApi } from "./gl/LinesEditor";
import { accountFromString } from "./gl/AccountPicker";
import { Badge, DataTable, GlState, LedgerBar, act, hint, linkBtn, money } from "./gl/glUi";
import {
  AppDropdown,
  EmptyState,
  Field,
  FormDrawer,
  LoadState,
  PageHeader,
  PrimaryButton,
  SecondaryButton,
  SlidePanel,
  Tabs,
  cardStyle,
  formatDate,
  inputStyle,
  pageStyle,
  todayYmd,
  useLoad,
} from "../../components/ui";

// Journals (accountants and the controller write; every Finance user can read): manual
// journals from draft to posted, recurring journals, foreign-currency revaluation and the
// accounting exceptions left by subledger events that could not be accounted.
const STATUS_FILTERS = [
  { label: "All statuses", value: "" },
  { label: "Draft", value: "DRAFT" },
  { label: "Pending approval", value: "PENDING_APPROVAL" },
  { label: "Approved", value: "APPROVED" },
  { label: "Posted", value: "POSTED" },
];
const PAGE = 50;
const statusOf = (j) => (j.reversed_by_id ? "REVERSED" : j.status);

function useCanWrite() {
  const { access } = useModuleAccess();
  return hasModulePermission(access, "FIN", "FIN_ACCOUNTANT", "FIN_CONTROLLER");
}

/* ------------------------------ journal editor ------------------------------ */

function useJournalOptions() {
  const cats = useLoad(() => finService.listCategories(), []);
  const opts = useFinOptions(["currencies", "rate-types"]);
  return {
    categories: (cats.data || []).filter((c) => c.status !== "INACTIVE").map((c) => ({ label: c.name, value: c.code })),
    currencies: (opts.currencies || []).map((o) => ({ label: o.label, value: o.code, digits: Number(o.row.precision_digits) })),
    rateTypes: [...(opts["rate-types"] || []).map((o) => ({ label: o.label, value: o.code })), { label: "User (type the rate)", value: "USER" }],
  };
}

function JournalEditor({ gl, journalId, onHide, onChanged }) {
  const canWrite = useCanWrite();
  const options = useJournalOptions();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null); // the journal as last loaded from the API
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = async (id) => {
    setLoadError(null);
    try {
      const res = await finService.getJournal(id);
      if (!res?.success) throw new Error(res?.message || "Could not load the journal");
      const j = res.data;
      setSaved(j);
      setForm({
        name: j.name,
        description: j.description || "",
        category_code: j.category_code,
        accounting_date: String(j.accounting_date).slice(0, 10),
        currency: j.currency,
        rate_type: j.rate_type || "",
        rate: j.rate_type === "USER" ? Number(j.rate) : "",
        auto_reverse: !!j.auto_reverse,
        lines: j.lines.map((l) => ({ account: accountFromString(gl.structure, l.account), description: l.description || "", entered_dr: Number(l.entered_dr) || "", entered_cr: Number(l.entered_cr) || "" })),
      });
    } catch (err) {
      setLoadError(err.message);
    }
  };

  useEffect(() => {
    if (journalId) load(journalId);
    else {
      setSaved(null);
      setForm({ name: "", description: "", category_code: "ADJUSTMENT", accounting_date: todayYmd(), currency: gl.ledger.currency, rate_type: "", rate: "", auto_reverse: false, lines: [blankLine(), blankLine()] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalId]);

  if (!form) {
    return (
      <SlidePanel show onHide={onHide} title="Journal" width={980}>
        <LoadState loading={!loadError} error={loadError} onRetry={() => load(journalId)} />
      </SlidePanel>
    );
  }

  const status = saved ? statusOf(saved) : "DRAFT";
  const editable = canWrite && status === "DRAFT";
  const foreign = form.currency !== gl.ledger.currency;
  const digits = (options.currencies.find((c) => c.value === form.currency) || {}).digits ?? 2;
  const totals = lineTotals(form.lines, digits);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const payload = () => ({
    ledger_id: gl.ledger.id,
    name: form.name,
    description: form.description || undefined,
    category_code: form.category_code,
    accounting_date: form.accounting_date,
    currency: form.currency,
    rate_type: foreign ? form.rate_type || undefined : undefined,
    rate: foreign && form.rate_type === "USER" ? Number(form.rate) : undefined,
    auto_reverse: form.auto_reverse ? 1 : 0,
    lines: linesForApi(form.lines),
  });

  // Every action re-reads the journal so the screen shows what the API now holds.
  const run = async (call, after) => {
    setBusy(true);
    const res = await act(call);
    setBusy(false);
    if (!res) return;
    onChanged();
    if (after === "close") onHide();
    else await load(saved ? saved.id : res.data.id);
  };

  const save = () => run(() => finService.saveJournal(saved ? saved.id : null, payload()));
  const footer = (
    <>
      <SecondaryButton onClick={onHide} disabled={busy}>
        Close
      </SecondaryButton>
      {saved && editable ? (
        <SecondaryButton disabled={busy} onClick={() => window.confirm("Delete this draft journal?") && run(() => finService.deleteJournal(saved.id), "close")}>
          Delete
        </SecondaryButton>
      ) : null}
      {editable ? (
        <SecondaryButton onClick={save} disabled={busy}>
          Save draft
        </SecondaryButton>
      ) : null}
      {saved && editable ? (
        <PrimaryButton disabled={busy || totals.diff !== 0} onClick={() => run(() => finService.submitJournal(saved.id))}>
          Submit
        </PrimaryButton>
      ) : null}
      {saved && canWrite && status === "APPROVED" ? (
        <PrimaryButton disabled={busy} onClick={() => run(() => finService.postJournal(saved.id))}>
          Post
        </PrimaryButton>
      ) : null}
      {saved && canWrite && status === "POSTED" && !saved.reversal_of_id ? (
        <PrimaryButton
          disabled={busy}
          onClick={() => {
            const date = window.prompt("Reverse on which accounting date? (YYYY-MM-DD, blank for the first day of the next period)", "");
            if (date === null) return;
            run(() => finService.reverseJournal(saved.id, date.trim() || undefined));
          }}
        >
          Reverse
        </PrimaryButton>
      ) : null}
    </>
  );

  return (
    <SlidePanel show onHide={() => !busy && onHide()} title={saved ? `Journal ${saved.je_number}` : "New journal"} width={980} footer={footer}>
      {saved ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <Badge status={status} />
          <span style={hint}>
            {saved.source_code} · {saved.period_name}
            {saved.reversal_of_number ? ` · reverses ${saved.reversal_of_number}` : ""}
            {saved.reversed_by_number ? ` · reversed by ${saved.reversed_by_number}` : ""}
          </span>
        </div>
      ) : null}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 16px" }}>
        <Field label="Name" required htmlFor="je-name">
          <input id="je-name" className="form-control" style={inputStyle} maxLength={150} value={form.name} disabled={!editable} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Category" required htmlFor="je-cat">
          <AppDropdown id="je-cat" value={form.category_code} options={options.categories} onChange={(e) => set({ category_code: e.value })} disabled={!editable} />
        </Field>
        <Field label="Accounting date" required htmlFor="je-date">
          <input id="je-date" type="date" className="form-control" style={inputStyle} value={form.accounting_date} disabled={!editable} onChange={(e) => set({ accounting_date: e.target.value })} />
        </Field>
        <Field label="Currency" required htmlFor="je-ccy">
          <AppDropdown id="je-ccy" value={form.currency} options={options.currencies} onChange={(e) => set({ currency: e.value })} disabled={!editable} filter />
        </Field>
        {foreign ? (
          <Field label={`Rate type (to ${gl.ledger.currency})`} htmlFor="je-rt" hint="Blank uses the corporate rate on the accounting date">
            <AppDropdown id="je-rt" value={form.rate_type || null} options={options.rateTypes} onChange={(e) => set({ rate_type: e.value })} disabled={!editable} />
          </Field>
        ) : null}
        {foreign && form.rate_type === "USER" ? (
          <Field label={`1 ${form.currency} = ? ${gl.ledger.currency}`} required htmlFor="je-rate">
            <input id="je-rate" type="number" min={0} step="any" className="form-control" style={inputStyle} value={form.rate} disabled={!editable} onChange={(e) => set({ rate: e.target.value })} />
          </Field>
        ) : null}
      </div>
      <Field label="Description" htmlFor="je-desc">
        <textarea id="je-desc" className="form-control" rows={2} maxLength={500} value={form.description} disabled={!editable} onChange={(e) => set({ description: e.target.value })} />
      </Field>
      <div className="form-check mb-3">
        <input id="je-rev" type="checkbox" className="form-check-input" checked={form.auto_reverse} disabled={!editable} onChange={(e) => set({ auto_reverse: e.target.checked })} />
        <label htmlFor="je-rev" className="form-check-label" style={{ fontSize: 14 }}>
          Reverse automatically on the first day of the next period (accruals)
        </label>
      </div>
      <LinesEditor lines={form.lines} onChange={(lines) => set({ lines })} structure={gl.structure} companyValue={gl.ledger.company_value} currency={form.currency} digits={digits} disabled={!editable} />
      {saved && foreign ? (
        <div style={hint}>
          Accounted in {gl.ledger.currency} at {Number(saved.rate)} ({saved.rate_type}, {formatDate(saved.rate_date)}): debits {fmtMoney(saved.total_accounted_dr)} / credits {fmtMoney(saved.total_accounted_cr)}.
        </div>
      ) : null}
      {saved && status === "PENDING_APPROVAL" ? <div style={{ ...hint, marginTop: 8 }}>Waiting for approval. Approvers find it in Approvals.</div> : null}
    </SlidePanel>
  );
}

/* ------------------------------ journal list ------------------------------ */

function JournalList({ gl }) {
  const canWrite = useCanWrite();
  const [filters, setFilters] = useState({ status: "", q: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(undefined); // undefined closed, null new, id
  const { data, loading, error, reload } = useLoad(
    () => finService.listJournals({ ledger_id: gl.ledger.id, status: filters.status || undefined, q: filters.q || undefined, date_from: filters.date_from || undefined, date_to: filters.date_to || undefined, page, size: PAGE }),
    [gl.ledger.code, filters.status, filters.q, filters.date_from, filters.date_to, page]
  );
  const rows = data || [];
  const setFilter = (patch) => {
    setPage(1);
    setFilters((f) => ({ ...f, ...patch }));
  };
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        {canWrite ? <PrimaryButton onClick={() => setOpen(null)}>New journal</PrimaryButton> : null}
        <div style={{ width: 190 }}>
          <AppDropdown id="je-status" value={filters.status} options={STATUS_FILTERS} onChange={(e) => setFilter({ status: e.value })} />
        </div>
        <input className="form-control" style={{ ...inputStyle, width: 200 }} placeholder="Number or name" aria-label="Search journals" defaultValue={filters.q} onKeyDown={(e) => e.key === "Enter" && setFilter({ q: e.target.value.trim() })} onBlur={(e) => e.target.value.trim() !== filters.q && setFilter({ q: e.target.value.trim() })} />
        <input type="date" className="form-control" style={{ ...inputStyle, width: 160 }} aria-label="From date" value={filters.date_from} onChange={(e) => setFilter({ date_from: e.target.value })} />
        <input type="date" className="form-control" style={{ ...inputStyle, width: 160 }} aria-label="To date" value={filters.date_to} onChange={(e) => setFilter({ date_to: e.target.value })} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No journals" text={canWrite ? "Create a journal, or change the filters." : "Nothing matches these filters."} />}
          columns={[
            { label: "Number", render: (r) => <span style={{ fontWeight: 600 }}>{r.je_number}</span> },
            { label: "Date", render: (r) => formatDate(r.accounting_date) },
            { label: "Period", render: (r) => r.period_name },
            { label: "Name", render: (r) => r.name },
            { label: "Source", render: (r) => r.source_code },
            { label: "Currency", render: (r) => r.currency },
            { label: "Amount", num: true, render: (r) => fmtMoney(r.total_entered_dr) },
            { label: "Status", render: (r) => <Badge status={statusOf(r)} /> },
          ]}
        />
        {page > 1 || rows.length === PAGE ? (
          <div className="d-flex justify-content-end gap-2 mt-2">
            <SecondaryButton disabled={page === 1} onClick={() => setPage(page - 1)}>
              Previous
            </SecondaryButton>
            <SecondaryButton disabled={rows.length < PAGE} onClick={() => setPage(page + 1)}>
              Next
            </SecondaryButton>
          </div>
        ) : null}
      </LoadState>
      {open !== undefined ? <JournalEditor gl={gl} journalId={open} onHide={() => setOpen(undefined)} onChanged={reload} /> : null}
    </>
  );
}

/* ------------------------------ recurring ------------------------------ */

function RecurringEditor({ gl, item, onHide, onDone }) {
  const options = useJournalOptions();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!item) {
        setForm({ name: "", category_code: "RECURRING", day_of_month: 1, next_run_date: todayYmd(), end_date: "", auto_post: true, description: "", lines: [blankLine(), blankLine()] });
        return;
      }
      const res = await finService.getRecurring(item.id);
      if (!alive || !res?.success) return;
      const r = res.data;
      setForm({
        name: r.name,
        category_code: r.category_code,
        day_of_month: r.day_of_month,
        next_run_date: String(r.next_run_date).slice(0, 10),
        end_date: r.end_date ? String(r.end_date).slice(0, 10) : "",
        auto_post: !!r.auto_post,
        description: r.description || "",
        lines: r.lines.map((l) => ({ account: accountFromString(gl.structure, l.account), description: l.description || "", entered_dr: Number(l.entered_dr) || "", entered_cr: Number(l.entered_cr) || "" })),
      });
    })();
    return () => {
      alive = false;
    };
  }, [item, gl.structure]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const save = async () => {
    setBusy(true);
    const res = await act(() =>
      finService.saveRecurring(item ? item.id : null, {
        ledger_id: gl.ledger.id,
        name: form.name,
        category_code: form.category_code,
        day_of_month: Number(form.day_of_month),
        next_run_date: form.next_run_date,
        end_date: form.end_date || null,
        auto_post: form.auto_post,
        description: form.description || undefined,
        lines: linesForApi(form.lines),
      })
    );
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };

  return (
    <SlidePanel
      show
      onHide={() => !busy && onHide()}
      title={item ? `Recurring journal – ${item.name}` : "New recurring journal"}
      width={980}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy || !form}>
            Save
          </PrimaryButton>
        </>
      }
    >
      {!form ? (
        <LoadState loading />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 16px" }}>
            <Field label="Name" required htmlFor="rj-name">
              <input id="rj-name" className="form-control" style={inputStyle} maxLength={150} value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field label="Category" required htmlFor="rj-cat">
              <AppDropdown id="rj-cat" value={form.category_code} options={options.categories} onChange={(e) => set({ category_code: e.value })} />
            </Field>
            <Field label="Day of month" required htmlFor="rj-day" hint="1 to 28, or 31 for the last day">
              <input id="rj-day" type="number" min={1} max={31} className="form-control" style={inputStyle} value={form.day_of_month} onChange={(e) => set({ day_of_month: e.target.value })} />
            </Field>
            <Field label="Next run" required htmlFor="rj-next">
              <input id="rj-next" type="date" className="form-control" style={inputStyle} value={form.next_run_date} onChange={(e) => set({ next_run_date: e.target.value })} />
            </Field>
            <Field label="Ends on" htmlFor="rj-end">
              <input id="rj-end" type="date" className="form-control" style={inputStyle} value={form.end_date} onChange={(e) => set({ end_date: e.target.value })} />
            </Field>
          </div>
          <div className="form-check mb-3">
            <input id="rj-post" type="checkbox" className="form-check-input" checked={form.auto_post} onChange={(e) => set({ auto_post: e.target.checked })} />
            <label htmlFor="rj-post" className="form-check-label" style={{ fontSize: 14 }}>
              Post each journal automatically (otherwise it is created as a draft for review)
            </label>
          </div>
          <LinesEditor lines={form.lines} onChange={(lines) => set({ lines })} structure={gl.structure} companyValue={gl.ledger.company_value} currency={gl.ledger.currency} />
        </>
      )}
    </SlidePanel>
  );
}

function Recurring({ gl }) {
  const canWrite = useCanWrite();
  const { data, loading, error, reload } = useLoad(() => finService.listRecurring(), []);
  const [edit, setEdit] = useState(undefined);
  const rows = data || [];
  const toggle = async (r) => (await act(() => finService.setRecurringStatus(r.id, r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"))) && reload();
  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-3">
        {canWrite ? <PrimaryButton onClick={() => setEdit(null)}>New recurring journal</PrimaryButton> : null}
        <span style={hint}>Rent, depreciation, standing accruals: created by the system on their day each month.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          empty={<EmptyState title="No recurring journals" />}
          columns={[
            { label: "Name", render: (r) => r.name },
            { label: "Day", render: (r) => (Number(r.day_of_month) === 31 ? "Last day" : r.day_of_month) },
            { label: "Next run", render: (r) => formatDate(r.next_run_date) },
            { label: "Ends", render: (r) => (r.end_date ? formatDate(r.end_date) : "—") },
            { label: "Amount", num: true, render: (r) => `${fmtMoney(r.amount)} ${r.currency}` },
            { label: "Posting", render: (r) => (r.auto_post ? "Automatic" : "Draft for review") },
            { label: "Status", render: (r) => <Badge status={r.status} /> },
            {
              label: "",
              render: (r) =>
                canWrite && r.status !== "COMPLETED" ? (
                  <span style={{ whiteSpace: "nowrap" }}>
                    <button type="button" style={linkBtn} onClick={() => setEdit(r)}>
                      Edit
                    </button>{" "}
                    <button type="button" style={linkBtn} onClick={() => toggle(r)}>
                      {r.status === "ACTIVE" ? "Pause" : "Resume"}
                    </button>
                  </span>
                ) : null,
            },
          ]}
        />
      </LoadState>
      {edit !== undefined ? <RecurringEditor gl={gl} item={edit} onHide={() => setEdit(undefined)} onDone={reload} /> : null}
    </>
  );
}

/* ------------------------------ revaluation ------------------------------ */

function Revaluation({ gl }) {
  const canWrite = useCanWrite();
  const periods = useLoad(() => finService.listPeriods(gl.ledger.id), [gl.ledger.code]);
  const opts = useFinOptions(["rate-types"]);
  const [show, setShow] = useState(false);
  const [result, setResult] = useState(null);
  const open = (periods.data || []).filter((p) => p.statuses.GL === "OPEN").map((p) => ({ label: p.period_name, value: p.id }));
  return (
    <>
      <div style={{ ...cardStyle, padding: 20, maxWidth: 720 }}>
        <div style={{ fontSize: 14, marginBottom: 12 }}>
          Restates foreign-currency balances on asset and liability accounts at the period-end rate. The difference posts to the ledger's unrealised exchange gain / loss accounts, and the journal reverses on the first day of the next period.
        </div>
        {canWrite ? <PrimaryButton onClick={() => setShow(true)}>Run revaluation</PrimaryButton> : <span style={hint}>Accountants run revaluations.</span>}
        {result ? (
          <div className="mt-3">
            {result.journals.length === 0 ? (
              <div style={hint}>Nothing to revalue: no foreign-currency balances in that range.</div>
            ) : (
              <DataTable
                rows={result.journals}
                rowKey={(r) => r.je_number}
                minWidth={480}
                columns={[
                  { label: "Currency", render: (r) => r.currency },
                  { label: "Rate", num: true, render: (r) => Number(r.rate) },
                  { label: "Gain", num: true, render: (r) => money(r.gain) || "—" },
                  { label: "Loss", num: true, render: (r) => money(r.loss) || "—" },
                  { label: "Journal", render: (r) => r.je_number },
                ]}
              />
            )}
          </div>
        ) : null}
      </div>
      <FormDrawer
        show={show}
        title="Run revaluation"
        initial={{ rate_type: "CORPORATE", account_low: "1000", account_high: "2999" }}
        onHide={() => setShow(false)}
        onSave={(clean) => finService.runRevaluation({ ...clean, ledger_id: gl.ledger.id })}
        onDone={(data) => setResult(data)}
        fields={[
          { key: "period_id", label: "Period", type: "select", required: true, options: open, hint: open.length ? undefined : "No open GL period" },
          { key: "account_low", label: "Accounts from", required: true, max: 25 },
          { key: "account_high", label: "Accounts to", required: true, max: 25 },
          { key: "rate_type", label: "Rate type", type: "select", required: true, options: (opts["rate-types"] || []).map((o) => ({ label: o.label, value: o.code })) },
          { key: "rate_date", label: "Rate date", type: "date", hint: "Blank uses the last day of the period" },
        ]}
      />
    </>
  );
}

/* ------------------------------ accounting exceptions ------------------------------ */

function Exceptions() {
  const [status, setStatus] = useState("ERROR");
  const { data, loading, error, reload } = useLoad(() => finService.listAccountingEvents({ status }), [status]);
  const [busy, setBusy] = useState(null);
  const retry = async (r) => {
    setBusy(r.id);
    await act(() => finService.processAccountingEvent(r.id));
    setBusy(null);
    reload();
  };
  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div style={{ width: 200 }}>
          <AppDropdown
            id="ev-status"
            value={status}
            options={[
              { label: "Could not account", value: "ERROR" },
              { label: "Waiting", value: "UNPROCESSED" },
              { label: "Accounted", value: "ACCOUNTED" },
            ]}
            onChange={(e) => setStatus(e.value)}
          />
        </div>
        <span style={hint}>Documents from sales, purchasing and other modules are accounted by the rules under Finance Setup → Accounting rules. Fix the rule (or open the period), then retry.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title={status === "ERROR" ? "No accounting exceptions" : "Nothing here"} />}
          columns={[
            { label: "Event", render: (r) => r.event_type },
            { label: "Document", render: (r) => r.document_number || "—" },
            { label: "Date", render: (r) => formatDate(r.event_date) },
            { label: "Status", render: (r) => <Badge status={r.status} /> },
            { label: "Problem", style: { maxWidth: 360, whiteSpace: "normal" }, render: (r) => r.error_text || "—" },
            { label: "Tries", num: true, render: (r) => r.attempts },
            {
              label: "",
              render: (r) =>
                r.status !== "ACCOUNTED" ? (
                  <button type="button" style={linkBtn} disabled={busy === r.id} onClick={() => retry(r)}>
                    Retry
                  </button>
                ) : null,
            },
          ]}
        />
      </LoadState>
    </>
  );
}

/* ------------------------------ page ------------------------------ */

export default function FinJournals() {
  const gl = useGlContext();
  const canWrite = useCanWrite();
  const [tab, setTab] = useState("journals");
  const tabs = [
    { key: "journals", label: "Journals" },
    { key: "recurring", label: "Recurring" },
    ...(canWrite ? [{ key: "reval", label: "Revaluation" }, { key: "exceptions", label: "Accounting exceptions" }] : []),
  ];
  return (
    <div style={pageStyle}>
      <PageHeader title="Journals" subtitle="Manual and system journals in the general ledger" />
      <GlState gl={gl}>
        <LedgerBar gl={gl} />
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
        {tab === "journals" ? <JournalList key={gl.ledger && gl.ledger.code} gl={gl} /> : null}
        {tab === "recurring" ? <Recurring gl={gl} /> : null}
        {tab === "reval" ? <Revaluation gl={gl} /> : null}
        {tab === "exceptions" ? <Exceptions /> : null}
      </GlState>
    </div>
  );
}

export { JournalEditor };
